/**
 * El expediente de una persona, y cómo lo editan las secciones.
 *
 * ⚠ **El tipo vive aquí y no en `FichaPersona`.** Las cuatro secciones lo necesitan
 * y la ficha las importa a ellas: dejarlo allí haría que cada sección importara de
 * su propio padre. Con `import type` eso no rompe en tiempo de ejecución, pero deja
 * un ciclo que la primera importación de valor sí convierte en fallo.
 */

export type Persona = {
  id: number
  nombres: string
  apellidos: string
  tipo_documento: string
  documento: string
  /** DIRECTIVA (19/10) es personal de la casa como EMPLEADO: lleva lo mismo en su ficha. */
  tipo: "EMPLEADO" | "DIRECTIVA" | "CLIENTE"
  estado: "ACTIVO" | "SUSPENDIDO" | "EGRESADO"
  cargo_id?: number | null
  /** (28/10) Si su cargo está en varios departamentos, el suyo. Nulo = el principal. */
  departamento_id?: number | null
  /** El NOMBRE, resuelto por el servidor. La pantalla guarda el `cargo_id`. */
  cargo: string | null
  telefono: string | null
  usuario_id: number | null
  tiene_foto: boolean
  fecha_nacimiento?: string | null
  genero?: string | null
  estado_civil?: string | null
  email_personal?: string | null
  /** El de la cuenta enlazada. Es el que sale impreso en la firma de correo; el
   *  personal no sale nunca. `null` si la ficha no tiene cuenta. */
  email_corporativo?: string | null
  /** El corporativo de la persona. Vacío = sale el de la empresa en la firma. */
  pais_telefono?: string | null
  telefono_corporativo?: string | null
  direccion?: string | null
  contacto_emergencia?: string | null
  telefono_emergencia?: string | null
  fecha_ingreso?: string | null
  cliente_id?: number | null
  /** Identificación y perfil (19/10, lector IA de personal). El RIF va «V123456789». */
  rif?: string | null
  rif_vence?: string | null
  cedula_vence?: string | null
  nacionalidad?: string | null
  profesion?: string | null
  resumen_curricular?: string | null
  estudios?: Estudio[]
  experiencias?: Experiencia[]
  notas?: string | null
  egreso_en?: string | null
  motivo_egreso?: string | null
}

export const NIVELES_ESTUDIO = [
  ["UNIVERSITARIO", "Universitario"],
  ["POSTGRADO", "Postgrado"],
  ["TECNICO", "Técnico"],
  ["BACHILLERATO", "Bachillerato"],
  ["PRIMARIA", "Primaria"],
  ["CURSO", "Curso o certificación"],
] as const

export type NivelEstudio = (typeof NIVELES_ESTUDIO)[number][0]

export type Estudio = {
  nivel: NivelEstudio
  titulo: string
  institucion: string | null
  anio_inicio: number | null
  anio_fin: number | null
  en_curso: boolean
}

/** `desde` y `hasta` son «AAAA» o «AAAA-MM»: un currículo rara vez dice el día. */
export type Experiencia = {
  empresa: string
  cargo: string | null
  desde: string | null
  hasta: string | null
  actual: boolean
  funciones: string | null
}

/**
 * Estudios y experiencia, editados como LISTA y guardados enteros.
 *
 * ⚠ **`null` es «no se tocó»**: entonces no viaja y el servidor no reemplaza nada.
 * Una lista vacía sí viaja, y quiere decir «no tiene ninguno».
 */
export type Curriculo = { estudios: Estudio[] | null; experiencias: Experiencia[] | null }

/**
 * Leer y escribir un campo del borrador.
 *
 * ⚠ **El borrador se queda en la ficha y las secciones sólo reciben estas dos
 * funciones.** Repartir el estado entre las secciones haría que «Guardar cambios»
 * tuviera que recomponerlo, y lo que se manda al servidor es exactamente lo que se
 * tocó: mandar el formulario entero dejaría una línea de bitácora con siete campos
 * cada vez que se corrige uno.
 */
export type Editor = {
  valor: (campo: keyof Persona) => string
  poner: (campo: string, v: string) => void
}
